import { useState } from "react";
import "../../assets/css/FullSignup/FullSignup.css";
import emailjs from "emailjs-com";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  BASE_URL,
  EMAIL_SERVICE_ID,
  EMAIL_TEMPLATE_ID,
  EMAIL_PUBLIC_KEY,
} from "../../store/config";

import ErrorModal from "../common/ErrorModal";

function FullSignup() {
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    countryCode: "+91",
    contact: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] =
    useState(false);

  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [modalType, setModalType] =
    useState("error");

  const [error, setError] = useState("");

  const [showErrorModal, setShowErrorModal] =
    useState(false);

  const [otpSent, setOtpSent] = useState(false);

  const [otp, setOtp] = useState("");

  const [otpVerified, setOtpVerified] =
    useState(false);

  const navigate = useNavigate();

  // PASSWORD VALIDATION
  const validatePassword = (password) => {
    if (password.length < 8)
      return "At least 8 characters required";

    if (!/[A-Z]/.test(password))
      return "Password must contain uppercase letter";

    if (!/[a-z]/.test(password))
      return "Password must contain lowercase letter";

    if (!/[0-9]/.test(password))
      return "Password must contain number";

    if (!/[!@#$%^&*]/.test(password))
      return "Password must contain special character";

    return null;
  };

  // GLOBAL MODAL MESSAGE
  const showMessage = (
    message,
    type = "error"
  ) => {
    setError(message);
    setModalType(type);
    setShowErrorModal(true);
  };

  // HANDLE INPUT CHANGES
  const handleChange = (e) => {
    const { name, value } = e.target;

    // RESET OTP IF EMAIL CHANGED
    if (name === "email") {
      setOtpSent(false);
      setOtp("");
      setOtpVerified(false);

      sessionStorage.removeItem("otpHash");
      sessionStorage.removeItem("otpExpiry");
    }

    // CONTACT NUMBER ONLY DIGITS
    if (name === "contact") {
      setFormData({
        ...formData,
        contact: value
          .replace(/\D/g, "")
          .slice(0, 10),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // GENERATE OTP
  const generateOtp = () =>
    Math.floor(
      100000 + Math.random() * 900000
    ).toString();

  // HASH OTP
  const hashOtp = async (otp) => {
    const encoder = new TextEncoder();

    const data = encoder.encode(otp);

    const hashBuffer =
      await crypto.subtle.digest(
        "SHA-256",
        data
      );

    return Array.from(
      new Uint8Array(hashBuffer)
    )
      .map((b) =>
        b.toString(16).padStart(2, "0")
      )
      .join("");
  };

  // SEND OTP EMAIL
  const sendOtpEmail = async (otp) => {
    return emailjs.send(
      EMAIL_SERVICE_ID,
      EMAIL_TEMPLATE_ID,
      {
        email: formData.email,
        passcode: otp,
      },
      EMAIL_PUBLIC_KEY
    );
  };

  // EMAIL VALIDATION
  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      email
    );

  // SEND OTP
  const handleSendOtp = async () => {
    if (!formData.email) {
      return showMessage(
        "Please enter email first"
      );
    }

    if (!isValidEmail(formData.email)) {
      return showMessage(
        "Invalid email address"
      );
    }

    try {
      const otpGenerated = generateOtp();

      const hashedOtp = await hashOtp(
        otpGenerated
      );

      // STORE HASHED OTP
      sessionStorage.setItem(
        "otpHash",
        hashedOtp
      );

      // OTP EXPIRE IN 2 MIN
      sessionStorage.setItem(
        "otpExpiry",
        Date.now() + 2 * 60 * 1000
      );

      // SEND EMAIL
      await sendOtpEmail(otpGenerated);

      setOtpSent(true);

      showMessage(
        "OTP sent successfully to your email",
        "success"
      );
    } catch (err) {
      console.error(err);

      showMessage("Failed to send OTP");
    }
  };

  // VERIFY OTP
  const handleVerifyOtp = async () => {
    if (!otp) {
      return showMessage("Enter OTP");
    }

    const storedHash =
      sessionStorage.getItem("otpHash");

    const expiry =
      sessionStorage.getItem("otpExpiry");

    // OTP EXPIRED
    if (
      !storedHash ||
      Date.now() > Number(expiry)
    ) {
      return showMessage("OTP expired");
    }

    const enteredHash = await hashOtp(otp);

    // OTP MATCH
    if (enteredHash === storedHash) {
      sessionStorage.removeItem("otpHash");

      sessionStorage.removeItem(
        "otpExpiry"
      );

      setOtpVerified(true);

      showMessage(
        "Email verified successfully",
        "success"
      );
    } else {
      setOtpVerified(false);

      showMessage("Invalid OTP");
    }
  };

  // SIGNUP SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    // OTP CHECK
    if (!otpVerified) {
      return showMessage(
        "Verify OTP first"
      );
    }

    // CONTACT VALIDATION
    if (
      !/^\d{10}$/.test(formData.contact)
    ) {
      return showMessage(
        "Contact must be 10 digits"
      );
    }

    // PASSWORD VALIDATION
    const passError = validatePassword(
      formData.password
    );

    if (passError) {
      return showMessage(passError);
    }

    // PASSWORD MATCH
    if (
      formData.password !==
      formData.confirmPassword
    ) {
      return showMessage(
        "Passwords do not match"
      );
    }

    try {
      // SIGNUP API
      const response = await axios.post(
        `${BASE_URL}api/auth/signup`,
        {
          fullname: formData.fullname,
          email: formData.email,
          contact_no: formData.contact,
          country_code:
            formData.countryCode,
          password: formData.password,
        }
      );

      // API RESPONSE
      const {
        access_token,
        is_first_user,
      } = response.data;

      // SAVE TOKEN
      localStorage.setItem(
        "access_token",
        access_token
      );

      // LOGIN STATUS
      localStorage.setItem(
        "isLoggedIn",
        "true"
      );

      // FIRST USER
      localStorage.setItem(
        "first_time_user",
        is_first_user
          ? "True"
          : "False"
      );

      // SUCCESS MESSAGE
      showMessage(
        "Account created successfully",
        "success"
      );

      // REDIRECT
      setTimeout(() => {
        navigate("/my-profile");
      }, 1500);
    } catch (err) {
      console.error(err);

      if (
        err.response?.status === 409
      ) {
        showMessage(
          "Email already registered"
        );
      } else {
        showMessage("Signup failed");
      }
    }
  };

  return (
    <div className="full-card">
      <h2 className="full-title">
        Create Account
      </h2>

      <form onSubmit={handleSubmit}>
        {/* FULL NAME */}
        <div className="full-field">
          <input
            type="text"
            name="fullname"
            placeholder="Full Name"
            value={formData.fullname}
            onChange={handleChange}
            required
          />
        </div>

        {/* EMAIL + OTP */}
        <div className="full-field full-email-otp">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          <button
            type="button"
            className="full-btn-otp"
            onClick={handleSendOtp}
            disabled={otpVerified}
          >
            {otpVerified
              ? "Verified"
              : "Send OTP"}
          </button>
        </div>

        {/* OTP VERIFY */}
        {otpSent && !otpVerified && (
          <div className="full-field full-otp-verify">
            <input
              type="text"
              value={otp}
              placeholder="Enter OTP"
              maxLength={6}
              onChange={(e) =>
                setOtp(
                  e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 6)
                )
              }
            />

            <button
              type="button"
              className="full-btn-otp"
              onClick={handleVerifyOtp}
            >
              Verify
            </button>
          </div>
        )}

        {/* CONTACT NUMBER */}
        <div className="full-field">
          <input
            type="text"
            name="contact"
            placeholder="Contact Number"
            value={formData.contact}
            onChange={handleChange}
            maxLength={10}
            required
          />
        </div>

        {/* PASSWORD */}
        <div className="full-field">
          <input
            type={
              showPassword
                ? "text"
                : "password"
            }
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>

        {/* CONFIRM PASSWORD */}
        <div className="full-field">
          <input
            type={
              showConfirmPassword
                ? "text"
                : "password"
            }
            name="confirmPassword"
            placeholder="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          className="full-btn"
        >
          Create Account
        </button>
      </form>

      {/* MODAL */}
      <ErrorModal
        show={showErrorModal}
        type={modalType}
        message={error}
        onClose={() => {
          setShowErrorModal(false);
          setError("");
        }}
      />
    </div>
  );
}

export default FullSignup;